/**
 * Editor state — React useReducer (per the spec; Zustand stays reserved for
 * global app stores). Holds layers, selection, canvas config and profile name.
 * A thin undo/redo history wraps the reducer: callers `commit()` right before a
 * change (or once at the start of a drag) to snapshot the prior state.
 */
import { useReducer, useRef, useState, useCallback, useMemo } from 'react';
import { DEFAULT_CANVAS, makeStarterLayers, uid } from './pgConstants';

const MAX_HISTORY = 60;

const initialState = () => {
  const canvasConfig = { ...DEFAULT_CANVAS };
  const layers = makeStarterLayers(canvasConfig);
  return { layers, selectedLayerId: layers[layers.length - 1]?.id || null, canvasConfig, profileName: 'Untitled profile' };
};

function reducer(state, action) {
  switch (action.type) {
    case 'ADD_LAYER': {
      const layers = [...state.layers, action.layer];
      return { ...state, layers, selectedLayerId: action.layer.id };
    }
    case 'UPDATE_LAYER': {
      const layers = state.layers.map((l) => (l.id === action.id ? { ...l, ...action.patch } : l));
      return { ...state, layers };
    }
    case 'UPDATE_LAYER_ADJUST': {
      const layers = state.layers.map((l) =>
        l.id === action.id ? { ...l, adjustments: { ...(l.adjustments || {}), ...action.patch } } : l);
      return { ...state, layers };
    }
    case 'DELETE_LAYER': {
      const layers = state.layers.filter((l) => l.id !== action.id);
      const selectedLayerId = state.selectedLayerId === action.id
        ? (layers[layers.length - 1]?.id || null) : state.selectedLayerId;
      return { ...state, layers, selectedLayerId };
    }
    case 'DUPLICATE_LAYER': {
      const src = state.layers.find((l) => l.id === action.id);
      if (!src) return state;
      const copy = { ...src, id: uid(), name: `${src.name} copy`, x: src.x + 16, y: src.y + 16,
        adjustments: src.adjustments ? { ...src.adjustments } : undefined };
      const idx = state.layers.findIndex((l) => l.id === action.id);
      const layers = [...state.layers];
      layers.splice(idx + 1, 0, copy);
      return { ...state, layers, selectedLayerId: copy.id };
    }
    case 'REORDER_LAYERS': {
      const layers = [...state.layers];
      const [moved] = layers.splice(action.from, 1);
      layers.splice(action.to, 0, moved);
      return { ...state, layers };
    }
    case 'SELECT_LAYER':
      return { ...state, selectedLayerId: action.id };
    case 'TOGGLE_VISIBILITY':
      return { ...state, layers: state.layers.map((l) => (l.id === action.id ? { ...l, visible: !l.visible } : l)) };
    case 'TOGGLE_LOCK':
      return { ...state, layers: state.layers.map((l) => (l.id === action.id ? { ...l, locked: !l.locked } : l)) };
    case 'UPDATE_CANVAS_CONFIG':
      return { ...state, canvasConfig: { ...state.canvasConfig, ...action.patch } };
    case 'SET_PROFILE_NAME':
      return { ...state, profileName: action.name };
    case 'LOAD_STATE':
      return { ...action.state };
    default:
      return state;
  }
}

export function useEditorState() {
  const [state, rawDispatch] = useReducer(reducer, undefined, initialState);
  const stateRef = useRef(state);
  stateRef.current = state;

  const past = useRef([]);
  const future = useRef([]);
  const [flags, setFlags] = useState({ canUndo: false, canRedo: false });
  const refreshFlags = useCallback(() => {
    setFlags({ canUndo: past.current.length > 0, canRedo: future.current.length > 0 });
  }, []);

  // Snapshot the present for undo. Call once before a change (or drag start).
  const commit = useCallback(() => {
    past.current.push(JSON.stringify(stateRef.current));
    if (past.current.length > MAX_HISTORY) past.current.shift();
    future.current = [];
    refreshFlags();
  }, [refreshFlags]);

  const undo = useCallback(() => {
    if (!past.current.length) return;
    future.current.push(JSON.stringify(stateRef.current));
    const prev = past.current.pop();
    rawDispatch({ type: 'LOAD_STATE', state: JSON.parse(prev) });
    refreshFlags();
  }, [refreshFlags]);

  const redo = useCallback(() => {
    if (!future.current.length) return;
    past.current.push(JSON.stringify(stateRef.current));
    const next = future.current.pop();
    rawDispatch({ type: 'LOAD_STATE', state: JSON.parse(next) });
    refreshFlags();
  }, [refreshFlags]);

  // Replace the whole scene (load a profile / apply a template) — clears history.
  const loadScene = useCallback((scene) => {
    past.current = []; future.current = [];
    rawDispatch({ type: 'LOAD_STATE', state: scene });
    refreshFlags();
  }, [refreshFlags]);

  // Bound action helpers. Discrete ops snapshot history automatically; the
  // live drag path uses updateLayer (no snapshot) after a single commit().
  const actions = useMemo(() => ({
    addLayer: (layer) => { commit(); rawDispatch({ type: 'ADD_LAYER', layer }); },
    updateLayer: (id, patch) => rawDispatch({ type: 'UPDATE_LAYER', id, patch }),
    patchLayer: (id, patch) => { commit(); rawDispatch({ type: 'UPDATE_LAYER', id, patch }); },
    updateAdjust: (id, patch) => rawDispatch({ type: 'UPDATE_LAYER_ADJUST', id, patch }),
    patchAdjust: (id, patch) => { commit(); rawDispatch({ type: 'UPDATE_LAYER_ADJUST', id, patch }); },
    deleteLayer: (id) => { commit(); rawDispatch({ type: 'DELETE_LAYER', id }); },
    duplicateLayer: (id) => { commit(); rawDispatch({ type: 'DUPLICATE_LAYER', id }); },
    reorderLayers: (from, to) => { commit(); rawDispatch({ type: 'REORDER_LAYERS', from, to }); },
    selectLayer: (id) => rawDispatch({ type: 'SELECT_LAYER', id }),
    toggleVisibility: (id) => { commit(); rawDispatch({ type: 'TOGGLE_VISIBILITY', id }); },
    toggleLock: (id) => { commit(); rawDispatch({ type: 'TOGGLE_LOCK', id }); },
    updateCanvas: (patch, history = true) => { if (history) commit(); rawDispatch({ type: 'UPDATE_CANVAS_CONFIG', patch }); },
    setProfileName: (name) => rawDispatch({ type: 'SET_PROFILE_NAME', name }),
  }), [commit]);

  return {
    state, stateRef, actions, commit, undo, redo, loadScene,
    canUndo: flags.canUndo, canRedo: flags.canRedo,
  };
}
